import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { SocialAuthButtons } from "../components/SocialAuthButtons";

const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryNext = new URLSearchParams(location.search).get("next");
  const redirectTo = queryNext || location.state?.from || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const u = await login(email, password);
      if (u && u.needs_onboarding) {
        navigate("/finish-registration", { replace: true });
        return;
      }
      // Admins land on dashboard unless a specific next param was provided.
      const dest = queryNext || (u.role === "admin" ? "/dashboard" : redirectTo);
      navigate(dest, { replace: true });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    if (queryNext) sessionStorage.setItem("postAuthNext", queryNext);
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-[var(--bg)] text-zinc-100 flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="text-xs uppercase tracking-[0.4em] text-[var(--accent)] inline-block mb-5">
          ← back
        </Link>
        <div className="flex items-center justify-center mb-4">
          <Logo to="/" size={44} />
        </div>
        <h1 className="text-4xl font-bold text-center">Welcome back.</h1>
        <p className="text-sm text-zinc-400 mt-2 text-center">Sign in to start guessing connectors.</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500">Email</label>
            <input
              data-testid="login-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full px-4 py-3 rounded-lg bg-zinc-900 border border-white/10 focus:border-[var(--accent)] outline-none transition"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500">Password</label>
            <input
              data-testid="login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full px-4 py-3 rounded-lg bg-zinc-900 border border-white/10 focus:border-[var(--accent)] outline-none transition"
            />
          </div>
          {err && (
            <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full px-5 py-3 rounded-full bg-[var(--accent)] text-black font-bold hover:brightness-110 disabled:opacity-50 transition"
          >
            {busy ? "signing in…" : "Sign in"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-6 text-xs text-zinc-500">
          <div className="flex-1 h-px bg-white/10" />
          OR
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <button
          type="button"
          data-testid="login-google-deprecated"
          style={{ display: "none" }}
          onClick={googleLogin}
        />
        <SocialAuthButtons onGoogle={googleLogin} testIdPrefix="login" />

        <p className="text-sm text-zinc-400 mt-6 text-center">
          No account?{" "}
          <Link
            to={queryNext ? `/register?next=${encodeURIComponent(queryNext)}` : "/register"}
            data-testid="link-to-register"
            className="text-[var(--accent)] hover:underline"
          >
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}
